import { useState } from 'react';
import { ApiError } from '@/shared/api/types';
import { useAnswerGap, useRejectGap, useResolveGap } from './useHelpScreen';
import type { GapView } from './gapView';

const STATUS_PILL: Record<string, string> = {
  open: 'bg-amber-100 text-amber-800',
  answered: 'bg-phblue-100 text-phblue-800',
  resolved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

function errorText(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    if (err.status === 409) return err.problem.detail ?? 'This gap is already resolved.';
    if (err.status === 422) return err.problem.detail ?? 'The answer was not accepted.';
    return err.message;
  }
  return err instanceof Error ? err.message : 'Request failed.';
}

/**
 * "Needs SME" callout for one help gap (ADR-0066). Internal instances only (the panel gates it
 * on testersHubEnabled + layer.internal). Answer / Resolve / Reject post to John's
 * `/api/help/gaps/_/*?id=` endpoints; each invalidates the route's layer, so the pill and the
 * stored answer refresh from the server.
 */
export function HelpGapCallout({ gap, route }: { gap: GapView; route: string }) {
  const [answer, setAnswer] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const answerM = useAnswerGap(route);
  const resolveM = useResolveGap(route);
  const rejectM = useRejectGap(route);
  const busy = answerM.isPending || resolveM.isPending || rejectM.isPending;
  const disabledReason = gap.fromServer ? undefined : 'Help server unavailable; answers cannot be saved right now.';
  const err = errorText(answerM.error ?? resolveM.error ?? rejectM.error);

  const submitAnswer = () => {
    const text = answer.trim();
    if (!text || busy) return;
    answerM.mutate({ id: gap.gapId, answerMd: text }, { onSuccess: () => setAnswer('') });
  };

  return (
    <div
      id={`help-gap-${gap.gapId}`}
      className="my-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs"
      data-testid="help-gap-callout"
      data-gap-id={gap.gapId}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-800">Needs SME</span>
        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${STATUS_PILL[gap.status] ?? 'bg-slate-100 text-slate-700'}`}>
          {gap.status}
        </span>
      </div>
      <p className="mt-1 text-slate-800">{gap.question}</p>
      {gap.issueId != null && <p className="mt-0.5 text-[10px] text-slate-500">Issue #{gap.issueId}</p>}
      {gap.answerMd && (
        <div className="mt-1.5">
          <div className="text-[10px] text-slate-500">
            Answer{gap.answeredBy ? ` by ${gap.answeredBy}` : ''}
          </div>
          <pre className="mt-0.5 whitespace-pre-wrap rounded border border-slate-200 bg-white p-1.5 font-sans text-slate-800">
            {gap.answerMd}
          </pre>
        </div>
      )}
      {gap.rejectNote && (
        <pre className="mt-1.5 whitespace-pre-wrap font-sans text-[10px] text-red-700">{gap.rejectNote}</pre>
      )}
      {gap.status !== 'resolved' && (
        <>
          <textarea
            aria-label={`Answer for ${gap.gapId}`}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            rows={3}
            maxLength={8000}
            placeholder="Your answer (Markdown)"
            disabled={!gap.fromServer}
            className="mt-1.5 w-full resize-y rounded border border-slate-300 bg-white px-2 py-1 text-xs focus:border-phblue-400 focus:outline-none focus:ring-1 focus:ring-phblue-400 disabled:bg-slate-100"
          />
          {rejecting && (
            <input
              type="text"
              aria-label={`Reject note for ${gap.gapId}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why is the answer rejected? (optional)"
              className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs"
            />
          )}
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={submitAnswer}
              disabled={!gap.fromServer || busy || answer.trim().length === 0}
              title={disabledReason}
              className="rounded bg-phblue-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-phblue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {answerM.isPending ? 'Saving…' : 'Answer'}
            </button>
            <button
              type="button"
              onClick={() => resolveM.mutate({ id: gap.gapId })}
              disabled={!gap.fromServer || busy}
              title={disabledReason}
              className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] text-slate-700 hover:border-green-500 hover:text-green-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Resolve
            </button>
            <button
              type="button"
              onClick={() => {
                if (!rejecting) {
                  setRejecting(true);
                  return;
                }
                rejectM.mutate(
                  { id: gap.gapId, note },
                  {
                    onSuccess: () => {
                      setRejecting(false);
                      setNote('');
                    },
                  },
                );
              }}
              disabled={!gap.fromServer || busy}
              title={disabledReason}
              className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] text-slate-700 hover:border-red-400 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {rejecting ? 'Confirm reject' : 'Reject'}
            </button>
            {rejecting && (
              <button
                type="button"
                onClick={() => setRejecting(false)}
                className="text-[11px] text-slate-500 hover:text-slate-800"
              >
                Cancel
              </button>
            )}
          </div>
          {!gap.fromServer && <p className="mt-1 text-[10px] text-slate-500">{disabledReason}</p>}
          {err && (
            <p className="mt-1 text-[11px] text-red-700" role="alert">
              {err}
            </p>
          )}
        </>
      )}
    </div>
  );
}
